import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Stack,
  CircularProgress, Box, Chip,
} from '@mui/material';
import { radius } from '../../themes/smartinventoryTheme';

// Read-only — GA follows up outside the app (email/phone), never replies
// in-app, so this dialog has no action beyond Close. `detail` starts as the
// list row (no screenshot) and is replaced once GET /ga/support/messages/:id
// resolves with the full document.
const SupportMessageDetailDialog = ({ detail, status, onClose, t, i18n }) => (
  <Dialog open={Boolean(detail)} onClose={onClose} maxWidth="sm" fullWidth>
    {detail && (
      <>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {t('ga.support.detailTitle', { defaultValue: 'Support message' })}
          {detail.context?.route && <Chip label={detail.context.route} size="small" />}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 2 }}>
            {detail.createdAt && new Date(detail.createdAt).toLocaleString(i18n.language)}
            {(detail.companyName || detail.username) && ' · '}
            {detail.companyName}
            {detail.companyName && detail.username && ' · '}
            {detail.username}
          </Typography>

          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{detail.message}</Typography>

          {status === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {status === 'error' && (
            <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
              {t('ga.error.generic')}
            </Typography>
          )}

          {detail.screenshotBase64 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('ga.support.screenshot', { defaultValue: 'Screenshot' })}
              </Typography>
              <Box
                component="img"
                src={`data:image/png;base64,${detail.screenshotBase64}`}
                alt={t('ga.support.screenshot', { defaultValue: 'Screenshot' })}
                sx={{ display: 'block', mt: 0.5, maxWidth: '100%', borderRadius: `${radius.md}px`, border: '1px solid', borderColor: 'divider' }}
              />
            </Box>
          )}

          {detail.context && Object.keys(detail.context).length > 0 && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                {t('ga.support.context', { defaultValue: 'Sent automatically' })}
              </Typography>
              <Stack divider={<Divider />} spacing={0.5} sx={{ mt: 0.5 }}>
                {Object.entries(detail.context)
                  .filter(([, value]) => value !== null && value !== undefined && value !== '')
                  .map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">{key}</Typography>
                      <Typography variant="caption" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Close', { defaultValue: 'Close' })}</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);

export default SupportMessageDetailDialog;
